import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { productionMethodsApi } from '@/api/productionMethodsApi';
import type { ProductFormState } from './types';

interface StepProductionMethodsProps {
  state: ProductFormState;
  updateState: (updater: ProductFormState | ((prev: ProductFormState) => ProductFormState)) => void;
}

export function StepProductionMethods({ state, updateState }: StepProductionMethodsProps) {
  const { data: methods, isLoading } = useQuery({
    queryKey: ['production-methods'],
    queryFn: () => productionMethodsApi.getAll(false),
  });

  const toggleMethod = (methodId: string) => {
    updateState((prev) => {
      const next = prev.productionMethodIds.includes(methodId)
        ? prev.productionMethodIds.filter((id) => id !== methodId)
        : [...prev.productionMethodIds, methodId];
      return { ...prev, productionMethodIds: next };
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600 dark:text-warm-sand">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading production methods...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
          Production Method
        </label>
        <p className="text-xs text-gray-600 dark:text-warm-sand/70 mb-4">
          Select all methods used to make this product. Materials and design steps depend on your selection.
        </p>
        {methods?.length === 0 ? (
          <p className="text-sm italic text-gray-600 dark:text-warm-sand">
            No production methods configured — add some in Catalog → Production Methods.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {methods?.map((method) => {
              const isSelected = state.productionMethodIds.includes(method.id);
              return (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => toggleMethod(method.id)}
                  className={`
                    inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all border-2
                    ${
                      isSelected
                        ? 'bg-amber-450/15 border-amber-450 text-amber-600 dark:text-amber-400'
                        : 'bg-white dark:bg-charcoal-800 border-gray-200 dark:border-charcoal-700 text-gray-700 dark:text-warm-sand hover:border-amber-450/40'
                    }
                  `}
                >
                  <span>{method.icon || '⚙️'}</span>
                  {method.name}
                  {isSelected && <CheckCircle2 className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
        )}
        {state.productionMethodIds.length > 0 && (
          <p className="mt-3 text-xs text-gray-600 dark:text-warm-sand/60">
            {state.productionMethodIds.length} method
            {state.productionMethodIds.length !== 1 ? 's' : ''} selected
          </p>
        )}
      </div>
    </div>
  );
}
